import { useState } from 'react';
import {
	FaBars,
	FaBrain,
	FaChartBar,
	FaChartLine,
	FaClock,
	FaComments,
	FaDollarSign,
	FaGlobe,
	FaPlug,
	FaShieldAlt,
	FaTimes,
	FaUser,
} from 'react-icons/fa';

import '../App.css';

// Import images
import LogoImg from '@/assets/static-landing-pages/Logo.png';
import ShopifyImg from '@/assets/static-landing-pages/Shopify.png';
import AiShakehandImg from '@/assets/static-landing-pages/ai-shakehand.png';
import CircuitImg from '@/assets/static-landing-pages/circuit.png';
import JoinImageImg from '@/assets/static-landing-pages/join-image.png';
import NegotiateGirlImg from '@/assets/static-landing-pages/negotiate-girl.png';
import RobotSlidesImg from '@/assets/static-landing-pages/robot-slides.png';
import ShopifyLogoImg from '@/assets/static-landing-pages/shopify-logo.png';
import StripeLogoImg from '@/assets/static-landing-pages/stripe-logo.png';
import UploadImg from '@/assets/static-landing-pages/upload.png';
import WoocommerceLogoImg from '@/assets/static-landing-pages/woocommerce-logo.png';

// Add smooth scrolling behavior
const scrollToSection = (sectionId: string) => {
	const element = document.getElementById(sectionId);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
};

function Landing() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const closeMobileMenu = () => {
		setIsMobileMenuOpen(false);
	};

	return (
		<div className="min-h-screen bg-white">
			{/* Header */}
			<header className="bg-white sticky top-0 z-50 shadow-sm">
				<nav className="w-full px-6 lg:px-70">
					<div className="flex items-center justify-between h-16">
						<div className="flex items-center">
							<img src={LogoImg} alt="Haggle" className="h-10 sm:h-12 w-auto" />
						</div>

						{/* Desktop Navigation */}
						<div className="hidden lg:flex items-center space-x-8">
							<button
								onClick={() => scrollToSection('contact')}
								className="text-gray-700 hover:scale-105 transition-all duration-200 text-base relative group cursor-pointer"
							>
								Contact
								<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-purpleBrand-500 transition-all duration-200 group-hover:w-full"></span>
							</button>
							<button
								onClick={() => scrollToSection('solution')}
								className="text-gray-700 hover:scale-105 transition-all duration-200 text-base relative group cursor-pointer"
							>
								Solution
								<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-purpleBrand-500 transition-all duration-200 group-hover:w-full"></span>
							</button>
							<button
								onClick={() => scrollToSection('pricing')}
								className="text-gray-700 hover:scale-105 transition-all duration-200 text-base relative group cursor-pointer"
							>
								Pricing
								<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-purpleBrand-500 transition-all duration-200 group-hover:w-full"></span>
							</button>
						</div>

						{/* Desktop Sign In Button */}
						<div className="hidden lg:block">
							<button
								className="bg-purpleBrand-500 text-white px-6 lg:px-8 py-2 rounded-full hover:scale-105 transition-all duration-200 text-sm lg:text-base cursor-pointer"
								onClick={() =>
									window.open(
										`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-in/`,
										'_blank',
									)
								}
							>
								Sign In
							</button>
						</div>

						{/* Mobile Menu Button */}
						<div className="lg:hidden">
							<button
								onClick={toggleMobileMenu}
								className="text-gray-700 hover:text-purple-600 transition-colors duration-200 p-2"
							>
								{isMobileMenuOpen ? (
									<FaTimes className="w-6 h-6" />
								) : (
									<FaBars className="w-6 h-6" />
								)}
							</button>
						</div>
					</div>

					{/* Mobile Menu */}
					{isMobileMenuOpen && (
						<div className="lg:hidden absolute top-16 left-0 right-0 bg-white shadow-lg border-t">
							<div className="px-4 py-4 space-y-4">
								<button
									onClick={() => {
										scrollToSection('contact');
										closeMobileMenu();
									}}
									className="block w-full text-left text-gray-700 hover:text-purple-600 transition-colors duration-200 py-2"
								>
									Contact
								</button>
								<button
									onClick={() => {
										scrollToSection('solution');
										closeMobileMenu();
									}}
									className="block w-full text-left text-gray-700 hover:text-purple-600 transition-colors duration-200 py-2"
								>
									Solution
								</button>
								<button
									onClick={() => {
										scrollToSection('pricing');
										closeMobileMenu();
									}}
									className="block w-full text-left text-gray-700 hover:text-purple-600 transition-colors duration-200 py-2"
								>
									Pricing
								</button>
								<button
									className="w-full bg-purpleBrand-500 text-white px-6 py-3 rounded-full transition-all duration-200 text-base cursor-pointer"
									onClick={() => {
										window.open(
											`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-in/`,
											'_blank',
										);
										closeMobileMenu();
									}}
								>
									Sign In
								</button>
							</div>
						</div>
					)}
				</nav>
			</header>

			{/* Hero Section */}
			<section
				className="relative py-10 sm:py-20 overflow-hidden min-h-screen flex"
				style={{
					backgroundImage: `url(${AiShakehandImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				{/* Overlay for better text readability */}
				<div className="absolute inset-0 bg-black opacity-70"></div>

				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col justify-center sm:justify-around">
					<div className="text-center mb-8 sm:mb-0">
						{/* Haggle Logo */}
						<div className="mb-6 sm:mb-8">
							<img
								src={LogoImg}
								alt="Haggle"
								className="h-16 sm:h-20 md:h-24 lg:h-32 w-auto mx-auto"
							/>
						</div>
					</div>

					{/* Business and Customer Sections */}
					<div className="text-center">
						{/* Main Headline */}
						<h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-8 sm:mb-12 lg:mb-16 px-4">
							AI-Powered Negotiation for E-Commerce
						</h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto px-4">
							<div className="text-center">
								<button
									className="bg-purpleBrand-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4"
									onClick={() => scrollToSection('customer-engagement')}
								>
									Business
								</button>
								<h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
									Win Real Customers
								</h3>
								<p className="text-purpleBrand-300 text-sm sm:text-base">
									Personalize Your Price | Boost Conversion
								</p>
							</div>
							<div className="text-center">
								<button
									className="bg-purpleBrand-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4"
									onClick={() =>
										window.open(
											`${import.meta.env.VITE_FRONTEND_URL}`,
											'_blank',
										)
									}
								>
									Customer
								</button>
								<h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
									Win Your Price
								</h3>
								<p className="text-purpleBrand-300 text-sm sm:text-base">
									Exclusive Events | Premium Products
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Customer Engagement Section */}
			<section
				id="customer-engagement"
				className="py-12 sm:py-16 lg:py-20 bg-gray-50 text-[#091C53]"
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 lg:mb-16 px-4">
							Customer Engagement with Event-Based Personalized Offering
						</h2>
						<div className="max-w-4xl mx-auto mb-6 sm:mb-8">
							<div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-2xl">
								<div className="aspect-video">
									<iframe
										width="100%"
										height="100%"
										src="https://www.youtube.com/embed/qopjPFERRc0"
										title="Haggle Demo Video"
										frameBorder="0"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
										allowFullScreen
										className="rounded-xl"
									></iframe>
								</div>
							</div>
						</div>
						<button
							className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
							onClick={() =>
								window.open(
									`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-up/`,
									'_blank',
								)
							}
						>
							Sign Up For Free
						</button>
					</div>
				</div>
			</section>

			{/* B2B2C SaaS Solution Section */}
			<section
				id="solution"
				className="py-12 sm:py-16 lg:py-20 bg-white text-[#091C53]"
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-8 sm:mb-12 lg:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 px-4 text-purpleBrand-500">
							B2B2C SaaS Solution
						</h2>
						<p className="text-base sm:text-lg text-gray-600 max-w-4xl mx-auto text-left px-4">
							Transform your e-commerce strategy with personalized, AI-driven
							price negotiations that boost revenue while delighting customers.
						</p>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
						{/* Left side - Robot Slides Image */}
						<div className="flex justify-center order-2 md:order-1">
							<img
								src={RobotSlidesImg}
								alt="AI Robot with Laptop Interface"
								className="max-w-full h-auto w-full max-w-md"
								style={{ maxHeight: '400px' }}
							/>
						</div>

						{/* Right side - Text content */}
						<div className="space-y-4 sm:space-y-3 text-center md:text-left order-1 md:order-2">
							<div>
								<p className="text-base sm:text-lg leading-relaxed">
									Haggle enables merchants to engage with shoppers one-on-one
									through an intuitive chatbot interface.
								</p>
							</div>
							<div>
								<p className="text-sm sm:text-base leading-relaxed">
									We've digitized the honoured tradition of marketplace
									bargaining, creating meaningful interactions that drive
									conversions and customer satisfaction.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Connect Your Store Section */}
			<section className="py-12 sm:py-16 lg:pb-20 bg-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-8 sm:mb-12 lg:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 lg:mb-16 px-4">
							Connect Your Store
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 max-w-4xl mx-auto">
							{/* Shopify Integration */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<img
										src={ShopifyImg}
										alt="Shopify Logo"
										className="w-40 sm:w-50 h-20 sm:h-25"
									/>
								</div>
								<h3 className="text-sm sm:text-base font-bold text-purpleBrand-500 mb-4 px-4">
									A listed App on Shopify App Store
								</h3>
								<button
									className="bg-purpleBrand-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
									onClick={() =>
										window.open(
											'https://apps.shopify.com/haggle-app?search_id=bf251aa0-29d7-4c7d-8062-2a82ac7f16e8&surface_detail=haggle&surface_inter_position=1&surface_intra_position=2&surface_type=search',
											'_blank',
										)
									}
								>
									Download For Free
								</button>
							</div>

							{/* General Upload */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<img
										src={UploadImg}
										alt="Upload Icon"
										className="w-40 sm:w-50 h-20 sm:h-24"
									/>
								</div>
								<h3 className="text-sm sm:text-base font-bold text-purpleBrand-500 mb-4 px-4">
									Not on Shopify? Upload Product list
								</h3>
								<button
									className="bg-purpleBrand-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
									onClick={() =>
										window.open(
											`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-up/`,
											'_blank',
										)
									}
								>
									Sign Up For Free
								</button>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Journey Section */}
			<section
				className="py-12 sm:py-16 lg:py-20 bg-white relative text-[#091C53]"
				style={{
					backgroundImage: `url(${CircuitImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				{/* Overlay for better text readability */}
				<div className="absolute inset-0 bg-white opacity-80"></div>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-8 sm:mb-12 lg:mb-16 px-4">
						The Journey Explained
					</h2>

					{/* Horizontal flow representation */}
					<div className="flex flex-col md:flex-row justify-center gap-4 sm:gap-6 relative">
						{/* Step 1 - Shortest bar */}
						<div className="mt-0 md:mt-[50px] flex flex-col items-center">
							<div
								className="h-3 sm:h-4 rounded-full mb-3"
								style={{
									width: '120px',
									backgroundColor: '#A7D9F7',
								}}
							></div>
							<h3 className="text-xs sm:text-sm font-semibold text-center max-w-40 sm:max-w-48 px-2">
								Merchant Creates a Haggle Event and Publishes
							</h3>
						</div>

						{/* Step 2 - Medium bar */}
						<div className="mt-0 md:mt-[35px] flex flex-col items-center">
							<div
								className="h-3 sm:h-4 rounded-full mb-3"
								style={{
									width: '120px',
									backgroundColor: '#F7A7A7',
								}}
							></div>
							<h3 className="text-xs sm:text-sm font-semibold text-center max-w-40 sm:max-w-48 px-2">
								Customer come to the event and Makes Offer
							</h3>
						</div>

						{/* Step 3 - Longer bar */}
						<div className="mt-0 md:mt-[25px] flex flex-col items-center">
							<div
								className="h-3 sm:h-4 rounded-full mb-3"
								style={{
									width: '120px',
									backgroundColor: '#D9A7F7',
								}}
							></div>
							<h3 className="text-xs sm:text-sm font-semibold text-center max-w-40 sm:max-w-48 px-2">
								Negotiation begins on an optimized basis of Supply & Demand
							</h3>
						</div>

						{/* Step 4 - Longest bar */}
						<div className="mt-0 md:mt-[5px] flex flex-col items-center">
							<div
								className="h-3 sm:h-4 rounded-full mb-3"
								style={{
									width: '120px',
									backgroundColor: '#A7F7A7',
								}}
							></div>
							<h3 className="text-xs sm:text-sm font-semibold text-center max-w-40 sm:max-w-48 px-2">
								A Win-Win Resolution, an Agree Price or a Graceful Exit
							</h3>
						</div>
					</div>
				</div>
			</section>

			{/* Benefits for Merchants */}
			<section
				className="py-12 sm:py-16 lg:py-20 text-[#091C53]"
				style={{ backgroundColor: '#F8F9FA' }}
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-8 sm:mb-12 lg:mb-16 px-4">
						Benefits for Merchants
					</h2>

					{/* Three Feature Columns */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 lg:mb-16">
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaChartLine
									className="w-6 h-6 sm:w-8 sm:h-8"
									style={{ color: '#A0AEC0' }}
								/>
							</div>
							<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">
								Capture Lost Sales
							</h3>
							<p className="text-xs sm:text-sm text-center px-4">
								Unlocking missed revenue opportunities that traditional fixed
								pricing leaves on the table.
							</p>
						</div>
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaDollarSign
									className="w-6 h-6 sm:w-8 sm:h-8"
									style={{ color: '#A0AEC0' }}
								/>
							</div>
							<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">
								Optimize Margins
							</h3>
							<p className="text-xs sm:text-sm text-center px-4">
								Move beyond "one-size-fits-all" discounts to personalized deals
								that maximize return on every transaction while meeting customer
								expectations.
							</p>
						</div>
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaGlobe
									className="w-6 h-6 sm:w-8 sm:h-8"
									style={{ color: '#A0AEC0' }}
								/>
							</div>
							<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">
								Gain Market Insights
							</h3>
							<p className="text-xs sm:text-sm text-center px-4">
								Access real-time data on market metrics, revealing valuable
								pricing intelligence that can inform your broader strategy.
							</p>
						</div>
					</div>

					{/* Two Statistics Columns */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
						<div className="text-center">
							<div
								className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-3 sm:mb-4"
								style={{ color: '#8B5CF6' }}
							>
								24%
							</div>
							<h3
								className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-4"
								style={{ color: '#2C2F4F' }}
							>
								Reduced Cart Abandonment
							</h3>
							<p className="text-xs sm:text-sm text-center px-4">
								Merchants using Haggle see significantly fewer customers leaving
								without completing purchases.
							</p>
						</div>
						<div className="text-center">
							<div
								className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-3 sm:mb-4"
								style={{ color: '#8B5CF6' }}
							>
								35%
							</div>
							<h3
								className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-4"
								style={{ color: '#2C2F4F' }}
							>
								Increased Return Rate
							</h3>
							<p className="text-xs sm:text-sm text-center px-4">
								Customers who successfully negotiate once are more likely to
								return and build loyalty.
							</p>
						</div>
					</div>

					{/* Call to Action Button */}
					<div className="text-center">
						<button
							className="bg-purpleBrand-500 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-colors cursor-pointer"
							onClick={() =>
								window.open(
									`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-up/`,
									'_blank',
								)
							}
						>
							Sign Up For Free
						</button>
					</div>
				</div>
			</section>

			{/* Benefits for Shoppers */}
			<section className="py-12 sm:py-16 lg:py-20 bg-white text-[#091C53]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-8 sm:mb-12 lg:mb-16 px-4">
						Benefits for Shoppers
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
						{/* Left side - Hero Image */}
						<div className="flex justify-center order-2 md:order-1">
							<div className="relative">
								<img
									src={NegotiateGirlImg}
									alt="Woman negotiating with AI shopping assistant"
									className="w-full max-w-sm sm:max-w-md h-auto"
								/>
							</div>
						</div>

						{/* Right side - Benefits */}
						<div className="space-y-6 sm:space-y-8 order-1 md:order-2">
							<div className="flex items-start space-x-3 sm:space-x-4">
								<div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaUser className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
								</div>
								<div>
									<h3 className="text-lg sm:text-xl font-medium mb-2">
										Personalized Pricing
									</h3>
									<p className="text-xs sm:text-sm">
										Customers are empowered by making personalized offers that
										fit their budget and perceived value.
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-3 sm:space-x-4">
								<div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaClock className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
								</div>
								<div>
									<h3 className="text-lg sm:text-xl font-medium mb-2">
										Frictionless Experience
									</h3>
									<p className="text-xs sm:text-sm">
										Enjoy quick, gamified negotiations without awkward human
										confrontations or lengthy back-and-forth exchanges.
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-3 sm:space-x-4">
								<div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaComments className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
								</div>
								<div>
									<h3 className="text-lg sm:text-xl font-medium mb-2">
										Satisfaction Boost
									</h3>
									<p className="text-xs sm:text-sm">
										The thrill of "winning" a personalized deal creates stronger
										brand affinity and drives repeat business.
									</p>
								</div>
							</div>
							<div className="text-center md:text-left">
								<button
									className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
									onClick={() =>
										window.open(
											`${import.meta.env.VITE_FRONTEND_URL}/`,
											'_blank',
										)
									}
								>
									Let's Find a Deal
								</button>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Technical Capabilities */}
			<section
				className="py-12 sm:py-15 lg:py-20 bg-white relative text-[#091C53]"
				style={{
					backgroundImage: `url(${CircuitImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div className="absolute inset-0 bg-white opacity-80"></div>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-center mb-8 sm:mb-10 px-4">
						Key Technical Capabilities
					</h2>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaBrain className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
							</div>
							<h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 px-2">
								Natural Language Processing
							</h3>
							<p className="text-xs sm:text-sm px-2">
								Our AI delivers intuitive, human-like interactions that feel
								natural and build customer rapport during negotiations.
							</p>
						</div>
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaPlug className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
							</div>
							<h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 px-2">
								Seamless Integration
							</h3>
							<p className="text-xs sm:text-sm px-2">
								Haggle connects effortlessly with your existing e-commerce and
								CRM platforms for unified operations and data flow.
							</p>
						</div>
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaChartBar className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
							</div>
							<h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 px-2">
								Insightful Dashboard
							</h3>
							<p className="text-xs sm:text-sm px-2">
								Track transaction history, conversion rates, and revenue impact
								with comprehensive analytics and reporting tools.
							</p>
						</div>
						<div className="text-center">
							<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
								<FaShieldAlt className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
							</div>
							<h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 px-2">
								Safe Transaction Environment
							</h3>
							<p className="text-xs sm:text-sm px-2">
								Industry-leading security infrastructure, ensuring all
								negotiations and payments are protected with enterprise-grade
								encryption.
							</p>
						</div>
					</div>

					{/* Integration Logos */}
					<div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8">
						<div className="flex items-center">
							<img
								src={ShopifyLogoImg}
								alt="Shopify"
								className="h-8 sm:h-10 lg:h-12 w-auto"
							/>
						</div>
						<div className="flex items-center">
							<img
								src={StripeLogoImg}
								alt="Stripe"
								className="h-8 sm:h-10 lg:h-12 w-auto"
							/>
						</div>
						<div className="flex items-center">
							<img
								src={WoocommerceLogoImg}
								alt="WooCommerce"
								className="h-8 sm:h-10 lg:h-12 w-auto"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Pricing Section */}
			<section id="pricing" className="py-12 sm:py-15 lg:py-20 text-[#091C53]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-2xl sm:text-3xl font-medium text-center mb-8 sm:mb-12 px-4">
						Transparent Pricing That Grows With You
					</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
						{/* Diamond Card */}
						<div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg relative">
							<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
								<div
									className="px-4 py-1 rounded-full text-sm font-bold"
									style={{
										backgroundColor: 'white',
										border: '1px solid #93C5FD',
										color: '#2563EB',
									}}
								>
									DIAMOND
								</div>
							</div>
							<div className="text-center pt-4 text-xs">
								<div
									className="text-2xl font-medium mb-2 bg-white"
									style={{ color: '#1A202C' }}
								>
									$799 +
								</div>
								<p className=" mb-4" style={{ color: '#4A5568' }}>
									Success Fee of 7.5% on Added Revenue
								</p>
								<ul className="text-left space-y-2 text-xs text-gray-600">
									<li>Ruby +</li>
									<li>5,000+ Negotiations per Month</li>
									<li>Hosting Haggle Events for Business Customers (B2B)</li>
									<li>Public, Semi-Private & Private Haggle Event Setting</li>
									<li>B2B Checkout</li>
									<li>Payment Integration for Supplier Pay out</li>
									<li>Enhanced Billing</li>
								</ul>
							</div>
						</div>

						{/* Ruby Card */}
						<div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg relative">
							<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
								<div
									className="px-4 py-1 rounded-full text-sm font-bold"
									style={{
										backgroundColor: 'white',
										border: '1px solid #FCA5A5',
										color: '#DC2626',
									}}
								>
									RUBY
								</div>
							</div>
							<div className="text-center pt-4 text-xs">
								<div
									className="text-2xl font-medium mb-2"
									style={{ color: '#1A202C' }}
								>
									$299 +
								</div>
								<p className=" mb-4" style={{ color: '#4A5568' }}>
									Success Fee of 7.5% on Added Revenue
								</p>
								<ul className="text-left space-y-2 text-xs text-gray-600">
									<li>Sapphire +</li>
									<li>Up to 5,000 Negotiations/Month</li>
									<li>Bulk Product Upload via CSV</li>
									<li>End to End negotiation on haggles.ai</li>
									<li>Full inventory Management</li>
									<li>Access to Supplier Haggle Events</li>
									<li>Favourite Supplier Event Notification</li>
								</ul>
							</div>
						</div>

						{/* Sapphire Card */}
						<div className="bg-white border border-purple-200 p-6 rounded-xl shadow-lg relative">
							<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
								<div className="bg-white border border-purple-300 text-purple-600 px-4 py-1 rounded-full text-sm font-bold">
									SAPPHIRE
								</div>
							</div>
							<div className="text-center pt-4 text-xs">
								<div
									className="text-2xl font-medium mb-2"
									style={{ color: '#1A202C' }}
								>
									$149 +
								</div>
								<p className=" mb-4" style={{ color: '#4A5568' }}>
									Success Fee of 7.5% on Added Revenue
								</p>
								<ul className="text-left space-y-2 text-xs text-gray-600">
									<li>Emerald +</li>
									<li>Up to 2,000 Negotiations per Month</li>
									<li>Branding Customization</li>
									<li>Bundling/Multiple SKUs Negotiation</li>
									<li>Priority Event Placement on Haggles.ai</li>
									<li>Abuse Limitation</li>
									<li>"Haggle Now" Banner on Listed Products</li>
									<li>Time-Boxed Checkout</li>
								</ul>
							</div>
						</div>

						{/* Emerald Card */}
						<div className="bg-white border border-green-200 p-6 rounded-xl shadow-lg relative">
							<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
								<div className="bg-white border border-green-300 text-green-600 px-4 py-1 rounded-full text-sm font-bold">
									EMERALD
								</div>
							</div>
							<div className="text-center pt-4 text-xs">
								<div
									className="text-2xl font-medium mb-2"
									style={{ color: '#1A202C' }}
								>
									FREE
								</div>
								<p className=" mb-4" style={{ color: '#4A5568' }}>
									$0 Success Fee on Added Revenue
								</p>
								<ul className="text-left space-y-2 text-xs text-gray-600">
									<li>1,000 Negotiations per Month</li>
									<li>Merchant Dashboard</li>
									<li>Rules Customization</li>
									<li>Expert Negotiator - Chatbot</li>
									<li>Customer Waitlist</li>
									<li>Post-Event Timed Final Offer</li>
									<li>Customer Notifications</li>
									<li>Event Preview on Haggles.ai</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Final CTA Section */}
			<section
				id="contact"
				className="py-8 sm:py-10 relative overflow-hidden flex items-center text-[#091C53]"
				style={{
					backgroundImage: `url(${JoinImageImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				{/* Dark overlay for better text readability */}
				<div className="absolute inset-0 bg-white opacity-80"></div>

				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
					<h2 className="text-2xl sm:text-3xl md:text-4xl font-medium mb-4 px-4">
						Join the Pioneers
					</h2>
					<p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-4xl mx-auto text-purple-700 px-4">
						In Shaping the Future of Personalized Pricing and Costumer
						Engagement in E-Commerce
					</p>

					<div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
						<p className="text-xs sm:text-sm mb-4 sm:mb-6">
							Ready to revolutionize your e-commerce experience? Unlock
							unrealized revenue with our intelligent negotiation tools that
							create win-win scenarios for both you and your customers.
						</p>
						<p className="text-xs sm:text-sm">
							Our team will guide you through implementation, from initial setup
							to ongoing optimization. Start capturing the value that
							traditional fixed pricing leaves behind.
						</p>
					</div>

					{/* Two buttons */}
					<div className="flex flex-col sm:flex-row gap-4 sm:gap-8 lg:gap-20 justify-center mb-8 sm:mb-16 px-4">
						<button
							className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer text-sm sm:text-base"
							onClick={() =>
								window.open(
									`${import.meta.env.VITE_FRONTEND_URL}/auth/sign-up/`,
									'_blank',
								)
							}
						>
							Sign Up For Free
						</button>
						<button
							className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer text-sm sm:text-base"
							onClick={() =>
								window.open('https://calendly.com/hamed-haggles', '_blank')
							}
						>
							Need a Demo?
						</button>
					</div>
				</div>
			</section>
		</div>
	);
}

export default Landing;
